import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Download, PaperPlaneTilt, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useAuth } from '../auth/AuthContext';
import { formatNzDateTime } from '../utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

/**
 * Admin-only page listing every successfully submitted (emailed) order with
 * one-click downloads of the bundle CSV and the inventory-system CSV. The
 * underlying records live in `submitted_orders` (persisted at email-send time).
 *
 * Customers also see this page but only their own submissions. Backend enforces
 * scope via `_user_can_access_bundles_for`.
 */
const SubmittedOrdersPage = () => {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const [prepayOnly, setPrepayOnly] = useState(false);

  const fetchPage = useCallback(async (skipVal, opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/submitted-orders`, {
        params: {
          limit: PAGE_SIZE,
          skip: skipVal,
          prepay_only: (opts.prepayOnly ?? prepayOnly) ? true : undefined,
        },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setSkip(skipVal);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [prepayOnly]);

  useEffect(() => { fetchPage(0); }, [fetchPage]);

  // Brave (and Firefox with strict popup settings) silently block programmatic
  // downloads from within the Emergent preview's sandboxed iframe — the JS
  // path completes but no save dialog appears. Plain anchors *do* work via
  // right-click → "Save link as", and once the app is deployed to production
  // (no iframe sandbox), direct left-clicks download normally too. The tip
  // below the page header tells users about the right-click workaround.
  const downloadUrl = (order, format) =>
    `${API}/submitted-orders/${order.id}/download?format=${format}`;

  const isCustomer = me?.role === 'customer';
  const showInventory = !isCustomer;
  const fmtDate = (iso) => formatNzDateTime(iso);

  return (
    <div className="flex-1 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-4xl font-bold font-heading text-text-primary mb-2 flex items-center gap-3"
                data-testid="submitted-orders-title">
              <PaperPlaneTilt size={32} weight="duotone" className="text-gold" />
              Submitted Orders
            </h1>
            <p className="text-text-secondary">
              {isCustomer
                ? 'Every order you’ve submitted, ready to re-download as CSV.'
                : 'Every successfully emailed order. Re-download either the bundle CSV (what customers see) or the inventory CSV (admin-only).'}
            </p>
          </div>
          {!isCustomer && (
            <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer"
                   data-testid="submitted-orders-prepay-filter-label">
              <input
                type="checkbox"
                checked={prepayOnly}
                onChange={(e) => { setPrepayOnly(e.target.checked); fetchPage(0, { prepayOnly: e.target.checked }); }}
                data-testid="submitted-orders-prepay-filter"
                className="accent-gold w-4 h-4"
              />
              💰 Show only prepay-flagged orders
            </label>
          )}
        </div>

        {error && (
          <div className="mb-4 px-3 py-2 text-sm text-danger bg-danger/10 border border-danger/30 rounded">{error}</div>
        )}
        <div
          className="mb-4 px-3 py-2 text-xs text-text-secondary bg-black/30 border border-dashed border-border-color rounded"
          data-testid="submitted-orders-download-hint"
        >
          💡 If Bundle/Inventory open a blank tab instead of downloading
          (some browsers like Brave block this inside the preview iframe),
          <strong className="text-text-primary"> right-click the button → &ldquo;Save link as&rdquo;</strong>.
          Direct-click downloads work normally in the deployed production app.
        </div>
        {msg && (
          <div data-testid="submitted-orders-status"
               className={`mb-4 px-3 py-2 text-sm rounded ${
                 msg.kind === 'ok'
                   ? 'text-success bg-success/10 border border-success/30'
                   : 'text-danger bg-danger/10 border border-danger/30'
               }`}>
            {msg.text}
          </div>
        )}

        {loading && <p className="text-text-secondary text-center py-8" data-testid="submitted-orders-loading">Loading…</p>}

        {!loading && items.length === 0 && (
          <div className="bg-surface border border-border-color rounded-xl p-12 text-center" data-testid="submitted-orders-empty">
            <PaperPlaneTilt size={48} weight="duotone" className="text-text-secondary mx-auto mb-3 opacity-50" />
            <p className="text-sm text-text-secondary">
              {isCustomer
                ? 'You haven’t submitted any orders yet — head to Browse & Order or Smart Saver to place one.'
                : 'No submitted orders yet. They’ll appear here as customers (or you on their behalf) confirm orders.'}
            </p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="bg-surface border border-border-color rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="submitted-orders-table">
                <thead className="bg-black/40 text-text-secondary text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Order #</th>
                    <th className="text-left px-4 py-3 font-semibold">When (NZ time)</th>
                    <th className="text-left px-4 py-3 font-semibold">Customer</th>
                    {!isCustomer && <th className="text-left px-4 py-3 font-semibold">Cust. Code</th>}
                    <th className="text-left px-4 py-3 font-semibold">Items</th>
                    {!isCustomer && <th className="text-left px-4 py-3 font-semibold">Submitted by</th>}
                    <th className="text-right px-4 py-3 font-semibold">Downloads</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((o, i) => {
                    const cust = o.customerSnapshot || {};
                    return (
                      <tr key={o.id} className="border-t border-border-color hover:bg-black/20"
                          data-testid={`submitted-order-row-${i}`}>
                        <td className="px-4 py-3 font-mono text-text-primary text-xs whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span>{o.orderNumber || '—'}</span>
                            {o.prepayRequested && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-warning/20 text-warning border border-warning/40 rounded text-[10px] font-bold"
                                data-testid={`prepay-pill-${i}`}
                                title="Customer requested prepayment terms"
                              >
                                💰 Prepay
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-text-secondary whitespace-nowrap">{fmtDate(o.submittedAt)}</td>
                        <td className="px-4 py-3 text-text-primary">
                          <div className="font-medium">{cust.name || '—'}</div>
                          <div className="text-xs text-text-secondary">{cust.email}</div>
                        </td>
                        {!isCustomer && (
                          <td className="px-4 py-3 font-mono text-xs text-text-secondary">
                            {cust.customerCode || <span className="text-warning">(none)</span>}
                          </td>
                        )}
                        <td className="px-4 py-3 text-text-secondary">
                          <span className="block">{o.bundleCount || 0} saver{o.bundleCount === 1 ? '' : 's'}</span>
                          {(o.looseItemCount || 0) > 0 && (
                            <span className="block text-xs">+ {o.looseItemCount} loose</span>
                          )}
                        </td>
                        {!isCustomer && (
                          <td className="px-4 py-3 text-xs text-text-secondary">{o.submittedBy || '—'}</td>
                        )}
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-2">
                            <a
                              href={downloadUrl(o, 'bundle')}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`download-submitted-bundle-${o.id}`}
                              className="px-3 py-1.5 text-xs text-text-secondary border border-border-color rounded hover:border-gold hover:text-gold inline-flex items-center gap-1.5"
                              title="Download the bundle invoice CSV (right-click → 'Save link as' if your browser doesn't start the download)"
                            >
                              <Download size={12} weight="bold" /> Bundle
                            </a>
                            {showInventory && (
                              <a
                                href={downloadUrl(o, 'inventory')}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`download-submitted-inventory-${o.id}`}
                                className="px-3 py-1.5 text-xs text-text-secondary border border-border-color rounded hover:border-gold hover:text-gold inline-flex items-center gap-1.5"
                                title="Download the inventory-system CSV (right-click → 'Save link as' if your browser doesn't start the download)"
                              >
                                <Download size={12} weight="bold" /> Inventory
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border-color text-xs text-text-secondary">
                <span data-testid="submitted-orders-pagination-info">
                  Showing {skip + 1}–{Math.min(skip + items.length, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fetchPage(Math.max(0, skip - PAGE_SIZE))}
                    disabled={skip === 0 || loading}
                    data-testid="submitted-orders-prev"
                    className="px-3 py-1 border border-border-color rounded hover:border-gold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                  >
                    <CaretLeft size={12} weight="bold" /> Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchPage(skip + PAGE_SIZE)}
                    disabled={skip + items.length >= total || loading}
                    data-testid="submitted-orders-next"
                    className="px-3 py-1 border border-border-color rounded hover:border-gold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                  >
                    Next <CaretRight size={12} weight="bold" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmittedOrdersPage;
